import type { NLPContext, Slots } from './context';
import { slotsSchema, nlpContextSchema } from './context';
import type { EndConversationReasons } from './endConversationAction';
import { endConversationReasonsSchema } from './endConversationAction';
import type { InterventionReason } from './interventionRequiredAction';
import { interventionReasonSchema } from './interventionRequiredAction';
import type { RequestFieldKey } from '../user-input';
import { requestFieldKeySchema } from '../user-input';
import type { ExtractValues, SchemaOf } from '../helpers';
import { Joi, objectSchemaOf } from '../helpers';
import type { InteractionMode } from './interactionMode';
import { interactionModeSchema } from './interactionMode';

export const conversationSubjectType = [
  'meeting',
  'delivery',
  'manager',
  'staffOnboard',
  'bathroom',
  'companion',
  'unknown',
] as const;

export type ConversationSubjectType = ExtractValues<
  typeof conversationSubjectType
>;

export const conversationSubjectTypeSchema = Joi.string().valid(
  ...conversationSubjectType
);

export const startMethod = ['manual', 'vision'] as const;
export type StartMethod = ExtractValues<typeof startMethod>;
export const startMethodSchema = Joi.string().valid(...startMethod);

export interface IConversation {
  conversationId: string;
  consoleId: string;
  isHandledByBot: boolean;
  isFinished: boolean;
  isMonitored?: boolean;
  isClaimed: boolean;
  startedOn: number;
  interactionMode?: InteractionMode;
  startedMonitoringOn?: number;
  requiresInterventionOn?: number;
  claimedOn?: number;
  finishedOn?: number;
  hilId?: string;
  interventionReason?: InterventionReason;
  interventionReasonDescription?: string;
  interventionIntent?: string;
  interventionIntentConfidence?: number;
  interventionMessageId?: string;
  interventionRecordingId?: string;
  endReason?: EndConversationReasons;
  endReasonDescription?: string;
  subject?: ConversationSubjectType;
  finalIntent?: string;
  slots?: Slots;
  nlpContextHistory?: NLPContext[];
  nlpContext?: NLPContext;
  isUserInputPending?: boolean;
  lastUserInputFieldKey?: RequestFieldKey;
  startMethod?: StartMethod;
  faceId?: string;
}

export const conversationRecordDbSchemaMap: SchemaOf<IConversation> = {
  conversationId: Joi.string().required(),
  consoleId: Joi.string().required(),
  isHandledByBot: Joi.bool().required(),
  isFinished: Joi.bool().required(),
  isMonitored: Joi.bool().optional(),
  isClaimed: Joi.bool().required(),
  startedOn: Joi.number().required(),
  interactionMode: interactionModeSchema.optional(),
  startedMonitoringOn: Joi.number().optional(),
  requiresInterventionOn: Joi.number(),
  claimedOn: Joi.number(),
  finishedOn: Joi.number(),
  hilId: Joi.string(),
  interventionReason: interventionReasonSchema,
  interventionReasonDescription: Joi.string(),
  interventionIntent: Joi.string(),
  interventionIntentConfidence: Joi.number(),
  interventionMessageId: Joi.string(),
  interventionRecordingId: Joi.string(),
  endReason: endConversationReasonsSchema,
  endReasonDescription: Joi.string(),
  subject: conversationSubjectTypeSchema,
  finalIntent: Joi.string(),
  slots: slotsSchema.optional(),
  nlpContextHistory: Joi.array().items(nlpContextSchema).optional(),
  nlpContext: nlpContextSchema.optional(),
  isUserInputPending: Joi.bool().optional(),
  lastUserInputFieldKey: requestFieldKeySchema.optional(),
  startMethod: Joi.string()
    .valid(...startMethod)
    .optional(),
  faceId: Joi.string().optional(),
};

export const conversationRecordDbSchema = objectSchemaOf<IConversation>(
  conversationRecordDbSchemaMap
);
